import type { Http } from "../http";
import { parseBody } from "../models/common";
import {
  compatibilityReadingSchema,
  contactMatchResultSchema,
  friendListSchema,
  friendSchema,
  socialProfileSchema,
  type CompatibilityReading,
  type ContactMatchResult,
  type Friend,
  type ReportReason,
  type SocialProfile,
} from "../models/social";

const EMPTY_MATCH: ContactMatchResult = { uploaded: 0, matches: [] };

/** Friends, handles and the two-chart reading. */
export function socialEndpoints(http: Http) {
  return {
    async fetchFriends(): Promise<readonly Friend[]> {
      return parseBody(friendListSchema, await http.get("/me/friends")).friends;
    },

    /**
     * Asks which hashes belong to people already here. Hashes only — no phone
     * number is ever a parameter. An empty batch never leaves the device.
     */
    async uploadContactHashes(hashes: readonly string[]): Promise<ContactMatchResult> {
      if (hashes.length === 0) return EMPTY_MATCH;
      const body = await http.post("/me/contacts", { hashes });
      return parseBody(contactMatchResultSchema, body);
    },

    /** Sends a request, or accepts the one waiting from this person. */
    async addFriend(userId: string): Promise<Friend> {
      return parseBody(friendSchema, await http.post(`/me/friends/${userId}`, {}));
    },

    async unfriend(userId: string): Promise<void> {
      await http.del(`/me/friends/${userId}`);
    },

    async blockUser(userId: string): Promise<void> {
      await http.post(`/me/block/${userId}`, {});
    },

    /** The server blocks them in the same call. */
    async reportUser(userId: string, reason: ReportReason, detail?: string): Promise<void> {
      const trimmed = detail?.trim();
      await http.post(`/me/report/${userId}`, trimmed ? { reason, detail: trimmed } : { reason });
    },

    async fetchSocialProfile(): Promise<SocialProfile> {
      return parseBody(socialProfileSchema, await http.get("/me/username"));
    },

    async setUsername(username: string): Promise<SocialProfile> {
      return parseBody(socialProfileSchema, await http.put("/me/username", { username }));
    },

    async setProfilePublic(isPublic: boolean): Promise<SocialProfile> {
      return parseBody(socialProfileSchema, await http.put("/me/profile-public", { public: isPublic }));
    },

    async registerPhone(e164: string): Promise<SocialProfile> {
      return parseBody(socialProfileSchema, await http.put("/me/phone", { phone: e164 }));
    },

    async fetchCompatibility(userId: string): Promise<CompatibilityReading> {
      return parseBody(compatibilityReadingSchema, await http.get(`/me/compatibility/${userId}`));
    },
  };
}
